import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import type { GrClaimRejectionEntry } from '@/lib/api';

interface RejectionTableProps {
  entries: GrClaimRejectionEntry[];
  /** When true, truncates the rejection-message column more aggressively for
   *  compact panels (e.g. the at-a-glance view on AdminGamesPage). */
  compact?: boolean;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

/**
 * Shared rendering of `gr_claim_rejection_log` rows. Used by:
 *   - AdminGamesPage's "Recent Claim Rejections" panel (compact view)
 *   - AdminRewardLogPage's "Rejected" tab (full view)
 *
 * Columns: Time, User (resolved username), Source (trust level + arche/app
 * id), Game, Event, Score, Code, Message. Username falls back to "unknown"
 * when the userId didn't resolve via batchGetUsernames upstream.
 *
 * Extracted per review I12 — the two surfaces previously rendered the same
 * markup with the same conditionals and column shapes.
 */
export function RejectionTable({ entries, compact = false }: RejectionTableProps) {
  const messageMaxClass = compact ? 'max-w-[20ch]' : 'max-w-[28ch]';
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Time</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Game</TableHead>
            <TableHead>Event</TableHead>
            <TableHead>Score</TableHead>
            <TableHead>Code</TableHead>
            <TableHead>Message</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((r) => (
            // Top-align every cell: the Source cell can stack three lines, so
            // uniform top alignment reads better than centering the shorter
            // cells against it.
            <TableRow key={r.rejectionId} className="[&>td]:align-top">
              <TableCell className="text-xs whitespace-nowrap">
                {formatTime(r.occurredAt)}
              </TableCell>
              <TableCell className="text-xs">
                {r.username ?? <span className="text-muted-foreground">unknown</span>}
              </TableCell>
              <TableCell className="text-xs">
                {/* Stack trust level, resolved name, and raw id. break-all lets
                    the 36-char id wrap instead of forcing the column (and the
                    whole table) wide. archeName is null whenever archeId is. */}
                <div className="flex flex-col">
                  <span className="font-medium">{r.trustLevel}</span>
                  {r.archeName ? <span className="text-foreground">{r.archeName}</span> : null}
                  {r.archeId ? (
                    <span className="text-muted-foreground break-all">{r.archeId}</span>
                  ) : null}
                  {r.oauthAppId ? (
                    <span className="text-muted-foreground break-all">{r.oauthAppId}</span>
                  ) : null}
                </div>
              </TableCell>
              <TableCell className="text-xs">{r.attemptedGameName ?? '—'}</TableCell>
              <TableCell className="text-xs">{r.gameEvent ?? '—'}</TableCell>
              <TableCell className="text-xs">{r.gameScore ?? '—'}</TableCell>
              <TableCell className="text-xs">
                <Badge variant="destructive">{r.rejectionCode}</Badge>
              </TableCell>
              <TableCell
                className={`text-xs ${messageMaxClass} truncate`}
                title={r.rejectionMessage}
              >
                {r.rejectionMessage}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
